import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Col, Flag, Grid, Icon, Spinner, Typography } from 'gu-ui-kit'
import styles from 'modules/User/Profile/subcomponents/Phone/phone.module.scss'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import { useCountries } from 'modules/Countries/hooks/useCountries'
import { Field, formValueSelector } from 'redux-form'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import renderSelect from 'components/FormControls/select'
import PropTypes from 'prop-types'
import renderInputField from 'components/FormControls/textInput'

const Phone = ({ autofill, form, submitting }) => {
  const { t } = useTranslation()
  const {
    user: { phone = null, phone_country_code = null, loading = false },
    countries: { data: countries = [] } = {},
  } = useSelector((state) => ({
    user: state['user/profile'],
    countries: state['countries'],
  }))
  const selector = formValueSelector(form)

  const { country_code, phone_mask } = useSelector((state) => ({
    ...selector(state, 'country_code', 'phone_mask'),
  }))
  const { getData, resetData } = useCountries()

  useEffect(() => {
    getData({ has_phone_codes: true })

    return () => resetData()
  }, [])

  useEffect(() => {
    phone_country_code && autofill('country_code', phone_country_code)
  }, [phone_country_code])

  useEffect(() => {
    phone && autofill('phone', phone)
  }, [phone])

  useEffect(() => {
    if (!countries.length || !phone || !phone_country_code) {
      return
    }

    const currentCountry = countries.find(
      ({ code }) => code === phone_country_code,
    )

    if (currentCountry) {
      const { code, phone: phoneCodeOptions = [] } = currentCountry
      const clearPhone = phone.replace('+', '')

      if (code) {
        autofill('country_code', code)
      }

      const phoneOption = phoneCodeOptions.find(
        ({ code }) => clearPhone.indexOf(code) === 0,
      )

      if (phoneOption) {
        const { mask } = phoneOption

        if (mask) {
          autofill('phone_mask', mask.replace(/[-]/g, ' '))
        }
      }
    }
  }, [countries, phone_country_code, phone])

  const countriesOptions = useMemo(
    () =>
      countries.reduce(
        (acc, { code, phone = [] }) => [
          ...acc,
          ...phone.map(({ code: phoneCode, mask: phoneMask = null }) => ({
            code,
            phoneCode,
            phoneMask,
            value: code,
            label: t(`countries::${code}`),
          })),
        ],
        [],
      ),
    [countries],
  )

  return (
    <Grid gap={16} wrap={false}>
      <Col flexGrow={0}>
        <Field
          readOnly
          name="country_code"
          mode={'filter'}
          component={renderSelect}
          dropdownClassName={styles['registration__select-dropdown']}
          showEmptyOption={false}
          noResultsLabel={t(
            'auth::forms.registration.fields.country_code.no_result',
          )}
          filterPlaceholder={t(
            'auth::forms.registration.fields.country_code.filter_placeholder',
          )}
          fullWidth
          t={t}
          options={countriesOptions}
          ControlProps={{
            startAdornment: country_code ? (
              <Flag className={'margin-left-16'} name={country_code} />
            ) : (
              <Icon
                className={'margin-left-16'}
                size={'m'}
                name={'globe'}
                color={'Label-Fifth'}
              />
            ),
          }}
          activeOptionLabelTemplate={({ phoneCode }) => `+${phoneCode}`}
          optionLabelTemplate={({ phoneCode, code }) => (
            <Grid
              justifyContent={'between'}
              alignItems={'center'}
              wrap={false}
              gap={16}
            >
              <Grid
                wrap={false}
                gap={8}
                justifyContent={'start'}
                alignItems={'center'}
              >
                <Flag name={code} />

                <Typography variant={'Body-15'} color={'Label-Fifth'}>
                  {t(`countries::${code}`)}
                </Typography>
              </Grid>

              <Typography noWrap variant={'Body-15'} color={'Label-Secondary'}>
                +{phoneCode}
              </Typography>
            </Grid>
          )}
          noLabel
        />
      </Col>
      <Col flexGrow={1}>
        <Field
          fullWidth
          readOnly
          normalize={phoneFieldNormalize}
          name="phone"
          component={!phone_country_code ? renderInputField : renderMaskedInput}
          placeholderChar={'\u2002'}
          mask={() =>
            phone_mask.split('').map((d) => (d === '9' ? /[0-9]/ : d))
          }
          inputMode={'tel'}
          label={t('auth::forms.registration.fields.phone.label')}
          t={t}
          InputProps={{
            startAdornment:
              loading || submitting ? (
                <Spinner width={16} color={'Label-GU-Primary'} />
              ) : null,
            //позже включим редактирование телефона
            // endAdornment: (
            //   <span
            //     className={styles['edit-button']}
            //     onClick={() => setReadOnly(!readOnly)}
            //   >
            //     {readOnly ? (
            //       <Icon
            //         noLabel
            //         name={'edit'}
            //         size={'m'}
            //         color={'Interface-Seventh'}
            //       />
            //     ) : (
            //       <Icon name={'check'} size={'m'} color={'Interface-Seventh'} />
            //     )}
            //   </span>
            // ),
          }}
        />
      </Col>
    </Grid>
  )
}

export default Phone

Phone.propTypes = {
  autofill: PropTypes.func,
  form: PropTypes.string,
  submitting: PropTypes.bool,
}
